import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/context/AuthContext";
import AdminPracticeHoursConfig from "@/components/admin/AdminPracticeHoursConfig";

function SupervisoryForms({ onError }) {
  const [learnerId, setLearnerId] = useState("");
  const [eligReason, setEligReason] = useState("");
  const [grantId, setGrantId] = useState("");
  const [revokeReason, setRevokeReason] = useState("");

  async function supervisoryEligibility(e) {
    e.preventDefault();
    onError("");
    try {
      await api.post("/access-workflow/admin/supervisory-eligibility", {
        learner_id: learnerId,
        reason: eligReason,
        external_reference: "",
      });
      setEligReason("");
    } catch (err) {
      onError(formatApiError(err));
    }
  }

  async function revokeGrant(e) {
    e.preventDefault();
    onError("");
    try {
      await api.post(`/access-workflow/admin/advanced-access/${grantId}/revoke`, { reason: revokeReason });
      setRevokeReason("");
    } catch (err) {
      onError(formatApiError(err));
    }
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <form onSubmit={supervisoryEligibility} className="space-y-2">
        <h3 className="font-medium text-sm">Supervisory eligibility</h3>
        <input className="input" placeholder="Learner ID" required value={learnerId} onChange={(e) => setLearnerId(e.target.value)} />
        <textarea className="input" rows={2} required minLength={8} placeholder="Reason" value={eligReason} onChange={(e) => setEligReason(e.target.value)} />
        <button type="submit" className="btn-outline">Record eligibility override</button>
      </form>
      <form onSubmit={revokeGrant} className="space-y-2">
        <h3 className="font-medium text-sm">Revoke advanced grant</h3>
        <input className="input" placeholder="Grant ID" required value={grantId} onChange={(e) => setGrantId(e.target.value)} />
        <textarea className="input" rows={2} required minLength={8} placeholder="Reason" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} />
        <button type="submit" className="btn-outline">Revoke grant (audited)</button>
      </form>
    </div>
  );
}

const INTEREST_LABELS = {
  general_learner: "General learner",
  manual_driving: "Manual driving",
  overseas_driver: "Overseas driver",
  older_driver_refresher: "Older driver / refresher",
  school_university: "School / university",
};

export default function AdminTrainingRequests() {
  const [requests, setRequests] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [eligibilityForm, setEligibilityForm] = useState({
    learner_id: "",
    basis: "paid_booking",
    external_reference: "",
    note: "",
  });

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    api
      .get("/access-workflow/admin/training-requests", { params: { status: "pending" } })
      .then((res) => setRequests(res.data.requests || []))
      .catch((err) => setError(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get("/admin/instructors").then((res) => setInstructors(res.data.instructors || [])).catch(() => {});
  }, [load]);

  async function assignRequest(requestId, instructorId) {
    setError("");
    try {
      await api.post(`/access-workflow/admin/training-requests/${requestId}/assign`, {
        instructor_id: instructorId,
      });
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  }

  async function recordEligibility(e) {
    e.preventDefault();
    setError("");
    try {
      await api.post("/access-workflow/admin/eligibility-verifications", eligibilityForm);
      setEligibilityForm((f) => ({ ...f, external_reference: "", note: "" }));
    } catch (err) {
      setError(formatApiError(err));
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-training-requests-page">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold text-primary">Admin — Training requests</h1>
        <p className="text-sm text-muted-foreground">
          Pending learner training interest. Assignment does not unlock advanced access — record eligibility, then the
          instructor activates the pathway.{" "}
          <Link to="/admin/learners" className="underline">Learner oversight</Link>
        </p>
      </header>

      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

      <section className="card space-y-4">
        <h2 className="text-lg font-semibold">Record eligibility (audited)</h2>
        <form onSubmit={recordEligibility} className="grid gap-3 md:grid-cols-2">
          <input
            className="input"
            placeholder="Learner Academy ID"
            required
            value={eligibilityForm.learner_id}
            onChange={(e) => setEligibilityForm({ ...eligibilityForm, learner_id: e.target.value })}
          />
          <select
            className="input"
            value={eligibilityForm.basis}
            onChange={(e) => setEligibilityForm({ ...eligibilityForm, basis: e.target.value })}
          >
            <option value="paid_booking">Paid booking (Trafft/payment reference)</option>
            <option value="agency_funded">Agency-funded (Operational approval)</option>
          </select>
          <input
            className="input md:col-span-2"
            placeholder="External reference"
            required
            minLength={4}
            value={eligibilityForm.external_reference}
            onChange={(e) => setEligibilityForm({ ...eligibilityForm, external_reference: e.target.value })}
          />
          <textarea
            className="input md:col-span-2"
            placeholder="Admin note (optional)"
            rows={2}
            value={eligibilityForm.note}
            onChange={(e) => setEligibilityForm({ ...eligibilityForm, note: e.target.value })}
          />
          <button type="submit" className="btn-accent md:col-span-2">Record verification</button>
        </form>
      </section>

      <section className="card space-y-4">
        <h2 className="text-lg font-semibold">Supervisory controls</h2>
        <p className="text-sm text-muted-foreground">
          Mandatory reason (8+ chars) is required for supervisory eligibility and grant revoke. Use qualifying-lesson
          verification to extend 90-day access — not feedback publish.
        </p>
        <SupervisoryForms onError={setError} />
      </section>

      <section className="card space-y-4">
        <h2 className="text-lg font-semibold">Pending requests ({requests.length})</h2>
        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!loading && requests.length === 0 && (
          <p className="text-sm text-muted-foreground">No pending training requests.</p>
        )}
        <ul className="space-y-4">
          {requests.map((req) => (
            <li key={req.id} className="border rounded-lg p-4 space-y-2" data-testid={`training-request-${req.id}`}>
              <p className="font-medium">
                {req.learner?.display_name || "Learner"} — {req.learner?.email || req.learner_id}
              </p>
              <p className="text-sm">
                Interest: {INTEREST_LABELS[req.interest] || req.interest}
                {req.note ? ` — ${req.note}` : ""}
              </p>
              <div className="flex flex-wrap gap-2 items-center">
                <select
                  className="input max-w-xs"
                  id={`assign-ins-${req.id}`}
                  defaultValue=""
                  aria-label="Instructor"
                >
                  <option value="" disabled>Select instructor</option>
                  {instructors.map((ins) => (
                    <option key={ins.id} value={ins.id}>{ins.display_name || ins.email}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn-accent"
                  onClick={() => {
                    const sel = document.getElementById(`assign-ins-${req.id}`);
                    if (sel?.value) assignRequest(req.id, sel.value);
                  }}
                >
                  Assign instructor
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
      <AdminPracticeHoursConfig />
    </div>
  );
}
